import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { LoginFormData, AdminLoginFormData, RegisterFormData, ChangePasswordFormData, User } from '@/types';
import { authService } from '@/services/api';
import { useErrorHandler } from '@/hooks/useErrorHandler';

interface AuthContextType {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginFormData) => Promise<void>;
  /** Login administrativo (e-mail + senha, sem código da instituição) */
  loginAdmin: (data: AdminLoginFormData) => Promise<void>;
  register: (data: RegisterFormData) => Promise<void>;
  logout: () => void;
  changePassword: (data: ChangePasswordFormData) => Promise<void>;
  updateProfile: (data: { nome?: string; email?: string; telefone?: string | null; cargo?: string | null }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { handleError, handleSuccess } = useErrorHandler();

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (storedToken) {
      setToken(storedToken);
    }

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Erro ao parsear dados do usuário:', error);
        localStorage.removeItem('user');
      }
    }

    setIsLoading(false);
  }, []);

  const login = async (data: LoginFormData) => {
    try {
      setIsLoading(true);
      const response = await authService.login(data.email, data.senha, data.codigoTenant.trim().toUpperCase());

      // Evita vazamento de dados de outra sessão/tenant.
      queryClient.clear();
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      handleSuccess('Login realizado com sucesso!');

      // Login SYS: navegação completa evita tela em branco no painel até remontar o app (equivalente a F5).
      if (data.codigoTenant.trim().toUpperCase() === 'SYS') {
        window.location.assign('/admin/planos');
        return;
      }
    } catch (error: any) {
      // Controle de tentativas no cliente (por email)
      const key = `loginAttempts:${data.email.toLowerCase()}`;
      const attempts = Number(localStorage.getItem(key) || '0') + 1;
      localStorage.setItem(key, String(attempts));

      const status = error?.response?.status;
      const retryAfter = error?.response?.headers?.['retry-after'] || error?.response?.headers?.['Retry-After'];
      const retryAfterSec = retryAfter ? Number(retryAfter) : undefined;

      if (status === 429) {
        const waitMsg = retryAfterSec
          ? `Muitas tentativas. Aguarde ${Math.ceil(retryAfterSec / 60)} min (${retryAfterSec}s) e tente novamente.`
          : 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
        handleError(new Error(waitMsg), 'Tentativas excedidas');
      } else {
        handleError(error, 'Erro ao fazer login');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginAdmin = async (data: AdminLoginFormData) => {
    try {
      setIsLoading(true);
      const response = await authService.loginAdmin(data.email, data.senha);

      // Evita reutilizar cache de sessão anterior.
      queryClient.clear();
      setToken(response.token);
      setUser(response.user);
      localStorage.setItem('token', response.token);
      localStorage.setItem('user', JSON.stringify(response.user));

      handleSuccess('Login administrativo realizado com sucesso!');
      window.location.assign('/admin/planos');
      return;
    } catch (error: any) {
      const key = `loginAttempts:${data.email.toLowerCase()}`;
      const attempts = Number(localStorage.getItem(key) || '0') + 1;
      localStorage.setItem(key, String(attempts));

      const status = error?.response?.status;
      const retryAfter = error?.response?.headers?.['retry-after'] || error?.response?.headers?.['Retry-After'];
      const retryAfterSec = retryAfter ? Number(retryAfter) : undefined;

      if (status === 429) {
        const waitMsg = retryAfterSec
          ? `Muitas tentativas. Aguarde ${Math.ceil(retryAfterSec / 60)} min (${retryAfterSec}s) e tente novamente.`
          : 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
        handleError(new Error(waitMsg), 'Tentativas excedidas');
      } else {
        handleError(error, 'Erro ao fazer login administrativo');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterFormData) => {
    try {
      setIsLoading(true);
      await authService.register(data.codigoTenant, data.nome, data.email, data.senha, data.telefone, data.cargo);
      handleSuccess('Usuário criado com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao criar usuário');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    queryClient.clear();
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    handleSuccess('Logout realizado com sucesso!');
  };

  const changePassword = async (data: ChangePasswordFormData) => {
    if (!token || !user) throw new Error('Usuário não autenticado');

    try {
      setIsLoading(true);
      await authService.changePassword(user.id, data.senhaAtual, data.novaSenha);
      handleSuccess('Senha alterada com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao alterar senha');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (data: { nome?: string; email?: string; telefone?: string | null; cargo?: string | null }) => {
    if (!token || !user) throw new Error('Usuário não autenticado');

    try {
      setIsLoading(true);
      const updatedUserResponse = await authService.updateProfile(user.id, data);
      const mergedUser = { ...user, ...updatedUserResponse };
      setUser(mergedUser);
      localStorage.setItem('user', JSON.stringify(mergedUser));
      handleSuccess('Perfil atualizado com sucesso!');
    } catch (error: any) {
      handleError(error, 'Erro ao atualizar perfil');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    token,
    user,
    isAuthenticated: !!token,
    isLoading,
    login,
    loginAdmin,
    register,
    logout,
    changePassword,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
